import moment from "moment";
import type { z } from "zod";

import {
  getTokenizedEquityDataApiUrl,
  getTokenizedEquityMarketDataSymbol,
  shouldUseTokenizedEquityMarketData,
} from "~/v2/helpers/tokenizedEquityTrading";
import instance from "..";
import { looseArray, looseParse } from "../looseParse";
import {
  clockSchema,
  cryptoAssetSchema,
  cryptoOrderbooksLatestSchema,
  type cryptoSnapshotSchema,
  cryptoSnapshotsSchema,
  getOptionSnapshotsResponseSchema,
  type latestQuoteSchema,
  marketStatusSchema,
  optionContractSchema,
  optionContractsResponseSchema,
  type quoteSchema,
  stockSnapshotSchema,
  stockSnapshotsResponseSchema,
} from "./data.schema";

export type Quote = z.infer<typeof quoteSchema>;

/** Quote shape returned by the Alpaca Market Data v2 quote endpoints */
export type LatestQuote = z.infer<typeof latestQuoteSchema>;

// ---------------------------------------------------------------------------
// Stock Snapshots – GET /v2/stocks/snapshots
// ---------------------------------------------------------------------------

/** Latest feed types accepted by latest/snapshot endpoints (includes "overnight") */
export type StockLatestFeed =
  | "sip"
  | "boats"
  | "overnight"
  | "delayed_sip"
  | "otc";

/** A single trade from a stock snapshot */
interface StockSnapshotTrade {
  /** Timestamp */
  t: string;
  /** Exchange */
  x: string;
  /** Price */
  p: number;
  /** Size */
  s: number;
  /** Trade ID */
  i: number;
  /** Condition flags */
  c: string[];
  /** Tape */
  z: "A" | "B" | "C" | "N" | "O";
}

/** OHLCV bar shared by stock and crypto snapshots */
interface SnapshotBar {
  /** Timestamp */
  t: string;
  /** Open */
  o: number;
  /** High */
  h: number;
  /** Low */
  l: number;
  /** Close */
  c: number;
  /** Volume */
  v: number;
  /** Trade count */
  n: number;
  /** VWAP */
  vw: number;
}

/** Shape of a single stock snapshot from madeira /v2/stocks/snapshots */
export type StockSnapshot = z.infer<typeof stockSnapshotSchema>;

/** Response shape from GET /v2/stocks/snapshots?symbols=... */
type StockSnapshotsResponse = z.infer<typeof stockSnapshotsResponseSchema>;

/**
 * Fetch stock snapshots for multiple symbols from madeira.
 * Returns latestTrade, latestQuote, minuteBar, dailyBar, and prevDailyBar
 * for each symbol in a single call.
 *
 * https://data.alpaca.markets/v2/stocks/snapshots
 */
export const getStockSnapshots = async (
  symbols: string[],
  feed?: StockLatestFeed,
): Promise<StockSnapshotsResponse> => {
  const params = new URLSearchParams({ symbols: symbols.join(",") });
  if (feed) {
    params.set("feed", feed);
  }

  const res = await instance.get<StockSnapshotsResponse>(
    `/stocks/snapshots?${params.toString()}`,
    { baseURL: window.env.DATA_API_URL },
  );

  return looseParse(
    stockSnapshotsResponseSchema,
    res.data,
    "getStockSnapshots",
  );
};

/**
 * Maps a madeira StockSnapshot into the legacy Quote interface.
 * This provides backward compatibility for all existing consumers
 * that depend on the Quote shape.
 *
 * When latestQuote is absent, bid/ask fields fall back to latestTrade
 * so consumers still receive a usable price.
 */
type SnapshotQuote = Pick<LatestQuote, "ap" | "as" | "bp" | "bs" | "t"> &
  Partial<Pick<LatestQuote, "ax" | "bx">>;

type SnapshotTrade = Pick<StockSnapshotTrade, "p" | "s" | "t"> &
  Partial<Pick<StockSnapshotTrade, "x">>;

type MappableSnapshot = {
  latestQuote?: SnapshotQuote;
  latestTrade?: SnapshotTrade;
  prevDailyBar?: Pick<SnapshotBar, "c">;
};

const isQuote = (quote: Quote | undefined): quote is Quote =>
  quote !== undefined;

const getQuoteMidpoint = (quote: SnapshotQuote | undefined) => {
  if (!quote) {
    return undefined;
  }

  const prices = [quote.bp, quote.ap].filter(
    (price): price is number => typeof price === "number" && price > 0,
  );

  if (prices.length === 0) {
    return undefined;
  }

  return prices.reduce((total, price) => total + price, 0) / prices.length;
};

const snapshotToQuote = (
  symbol: string,
  snap: MappableSnapshot,
  useQuoteExchange = true,
): Quote | undefined => {
  const quote = snap.latestQuote;
  const trade = snap.latestTrade;
  const fallbackPrice = trade?.p ?? getQuoteMidpoint(quote);
  if (fallbackPrice === undefined) {
    return undefined;
  }

  const fallbackTimestamp = trade?.t ?? quote?.t ?? "";
  const tradeExchange = trade?.x ?? "";

  return {
    symbol,
    bid_timestamp: quote?.t ?? fallbackTimestamp,
    bid: quote?.bp ?? fallbackPrice,
    bid_size: quote?.bs ?? 0,
    bid_exchange: useQuoteExchange ? (quote?.bx ?? tradeExchange) : "",
    ask_timestamp: quote?.t ?? fallbackTimestamp,
    ask: quote?.ap ?? fallbackPrice,
    ask_size: quote?.as ?? 0,
    ask_exchange: useQuoteExchange ? (quote?.ax ?? tradeExchange) : "",
    last_timestamp: fallbackTimestamp,
    last: fallbackPrice,
    last_size: trade?.s ?? 0,
    last_exchange: tradeExchange,
    last_day_closing: String(snap.prevDailyBar?.c ?? 0),
  };
};

/**
 * Maps a CryptoSnapshot into the legacy Quote interface.
 * Crypto snapshots don't have exchange fields, so those default to "".
 * Falls back to latestTrade when latestQuote is absent.
 */
const cryptoSnapshotToQuote = (symbol: string, snap: CryptoSnapshot) =>
  snapshotToQuote(symbol, snap, false);

const isCryptoSymbol = (symbol: string) =>
  symbol.includes("/") || shouldUseTokenizedEquityMarketData(symbol);
const isOptionSymbol = (symbol: string) =>
  symbol.length > 12 && !symbol.includes("/");

/**
 * Maps an option snapshot (from GetOptionSnapshotsResponse) into the legacy Quote interface.
 * prevDailyBar is optional on options - defaults to "0" if absent.
 * Falls back to latestTrade when latestQuote is absent.
 */
const optionSnapshotToQuote = (
  symbol: string,
  snap: GetOptionSnapshotsResponse["snapshots"][string],
) => snapshotToQuote(symbol, snap);

// ---------------------------------------------------------------------------
// Legacy quote endpoints removed (LPCD-1783):
// - getHistoricalQuotes / getHistoricalQuote (never called)
// - getLatestQuotes / getLatestQuote (replaced by getStockSnapshots in LPCD-1773)
// ---------------------------------------------------------------------------

/**
 * Fetch quotes for multiple symbols using madeira snapshots.
 * Returns Quote[] for backward compatibility with all existing consumers.
 *
 * Routes each symbol to the correct snapshot endpoint based on asset type:
 * - Crypto (contains "/")  → CRYPTO_DATA_API_URL /snapshots
 * - Options (length > 12)  → OPTION_CHAIN_DATA_API_URL /snapshots
 * - Stocks (default)       → DATA_API_URL /stocks/snapshots
 */
export const getQuotes = async (
  symbols: string[],
  feed?: StockLatestFeed,
): Promise<Quote[]> => {
  const stockSymbols = symbols.filter(
    (s) => !isCryptoSymbol(s) && !isOptionSymbol(s),
  );
  const cryptoPairSymbols = symbols.filter(
    (s) => s.includes("/") && !shouldUseTokenizedEquityMarketData(s),
  );
  const tokenizedEquitySymbols = symbols.filter(
    shouldUseTokenizedEquityMarketData,
  );
  const optionSymbols = symbols.filter(isOptionSymbol);

  const requests: Promise<Quote[]>[] = [];

  if (stockSymbols.length > 0) {
    requests.push(
      getStockSnapshots(stockSymbols, feed).then((snapshots) =>
        stockSymbols
          .filter((s) => snapshots[s])
          .map((s) => snapshotToQuote(s, snapshots[s]))
          .filter(isQuote),
      ),
    );
  }

  if (cryptoPairSymbols.length > 0) {
    requests.push(
      getCryptoSnapshots({ symbols: cryptoPairSymbols }).then((data) =>
        cryptoPairSymbols
          .filter((s) => data.snapshots[s])
          .map((s) => cryptoSnapshotToQuote(s, data.snapshots[s]))
          .filter(isQuote),
      ),
    );
  }

  if (tokenizedEquitySymbols.length > 0) {
    const tokenizedEquityDataApiUrl = getTokenizedEquityDataApiUrl();
    if (tokenizedEquityDataApiUrl) {
      const snapshotSymbols = tokenizedEquitySymbols.map(
        (symbol) => getTokenizedEquityMarketDataSymbol(symbol) ?? symbol,
      );
      requests.push(
        getCryptoSnapshots(
          { symbols: snapshotSymbols },
          tokenizedEquityDataApiUrl,
        ).then((data) =>
          tokenizedEquitySymbols
            .map((_requestedSymbol, index) => {
              const snapshotSymbol = snapshotSymbols[index];
              const snap = data.snapshots[snapshotSymbol];
              if (!snap) {
                return undefined;
              }
              return cryptoSnapshotToQuote(snapshotSymbol, snap);
            })
            .filter((quote): quote is Quote => !!quote && isQuote(quote)),
        ),
      );
    }
  }

  if (optionSymbols.length > 0) {
    requests.push(
      getOptionContractSnapshot({ symbols: optionSymbols }).then((data) =>
        optionSymbols
          .filter((s) => data.snapshots[s])
          .map((s) => optionSnapshotToQuote(s, data.snapshots[s]))
          .filter(isQuote),
      ),
    );
  }

  const results = await Promise.all(requests);
  return results.flat();
};

/**
 * Fetch a single quote using madeira snapshots.
 * Convenience wrapper that returns a single Quote or undefined.
 */
export const getQuote = async (
  symbol: string,
  feed?: StockLatestFeed,
): Promise<Quote | undefined> => {
  const quotes = await getQuotes([symbol], feed);
  return quotes[0];
};

/**
 * Fetch a SIP snapshot for a single symbol via the trading-api endpoint.
 * Used for unsubscribed users at the order review step.
 * Rate-limited to 6 requests/minute (enforced server-side, returns 429).
 *
 * GET /api/v1/marketdata/sip-snapshots/{symbol}
 */
export const getSipSnapshot = async (
  symbol: string,
): Promise<Quote | undefined> => {
  const res = await instance.get<
    Pick<StockSnapshot, "latestQuote" | "latestTrade" | "prevDailyBar">
  >(`/marketdata/sip-snapshots/${symbol}`, { baseURL: "/api/v1" });
  return snapshotToQuote(
    symbol,
    looseParse(stockSnapshotSchema, res.data, "getSipSnapshot"),
  );
};

export type Clock = z.infer<typeof clockSchema>;

export const getClock = async (): Promise<Clock> => {
  // Clock is served by kairos at /internal/clock, not trading-api.
  const res = await instance.get("/clock", {
    baseURL: window.env.API_URL,
  });
  return looseParse(clockSchema, res.data, "getClock");
};

export type MarketStatusPeriod = MarketStatus["current"];

export type MarketPhase = MarketStatus["phase"];

export type MarketStatus = z.infer<typeof marketStatusSchema>;

export const getMarketStatus = async (
  market: string = "NMS",
): Promise<MarketStatus> => {
  // Cache-bust: the backend caches this response improperly.
  // Adding _t ensures CDN/proxy never serves a stale phase.
  const res = await instance.get(`/markets/${market}/status`, {
    params: { _t: Date.now() },
  });
  return looseParse(marketStatusSchema, res.data, "getMarketStatus");
};

export type CryptoOrderbooksLatest = z.infer<
  typeof cryptoOrderbooksLatestSchema
>;

export type CryptoOrderbook = CryptoOrderbooksLatest["orderbooks"][string];

export type CryptoOrderbookEntry = CryptoOrderbook["b"][number];

// get orderbooks
export const getOrderbooks = async (symbols: string[]) => {
  const res = await instance.get<CryptoOrderbooksLatest>(
    `/latest/orderbooks?symbols=${symbols.join(",")}`,
    {
      baseURL: window.env.CRYPTO_DATA_API_URL,
    },
  );
  return looseParse(cryptoOrderbooksLatestSchema, res.data, "getOrderbooks");
};

export type CryptoSnapshot = z.infer<typeof cryptoSnapshotSchema>;

export type CryptoSnapshots = z.infer<typeof cryptoSnapshotsSchema>;

export interface GetCryptoSnapshotParams {
  symbols: string[];
}

// get crypto snapshot
export const getCryptoSnapshots = async (
  params: GetCryptoSnapshotParams,
  baseURL: string = window.env.CRYPTO_DATA_API_URL,
): Promise<CryptoSnapshots> => {
  const res = await instance.get(
    `/snapshots?symbols=${params.symbols.join(",")}`,
    {
      baseURL,
    },
  );
  return looseParse(cryptoSnapshotsSchema, res.data, "getCryptoSnapshots");
};

export type CryptoAsset = z.infer<typeof cryptoAssetSchema>;

// we have to use this for now; not exposed on staging
export const getCryptoAssets = (): Promise<CryptoAsset[]> =>
  instance
    .get("/assets/public/beta", {
      baseURL: "https://api.alpaca.markets/v2",
    })
    .then(({ data }) =>
      looseParse(
        looseArray(cryptoAssetSchema, "getCryptoAssets"),
        data,
        "getCryptoAssets",
      ),
    );

export type OptionContract = z.infer<typeof optionContractSchema>;

export type OptionContractsResponse = z.infer<
  typeof optionContractsResponseSchema
>;

type OptionsContractsParams = {
  underlying_symbol?: string;
  expiration_date_gte?: string;
  expiration_date?: string;
  type?: "call" | "put";
};

/**
 * Contracts are public reference data — the listed OCC contracts for an
 * underlying, identical whichever account is asking — and the paper API is the
 * endpoint that serves them. The broker backend behind the default instance has
 * no such route and answers 404, so this never varies by account.
 *
 * The path is relative to `PAPER_API_ENDPOINT`, which already points at the
 * origin's `/v2` in every deployed environment. Spelling the prefix out here as
 * well asks for `/v2/v2/options/contracts` and answers 404 everywhere but local.
 */
const optionsContractsRequest = (suffix = "") => ({
  url: `/options/contracts${suffix}`,
  config: { baseURL: window.env.PAPER_API_ENDPOINT },
});

export const getOptionsContracts = async ({
  underlying_symbol,
  expiration_date_gte = moment().format("YYYY-MM-DD"),
  expiration_date,
  type,
}: OptionsContractsParams): Promise<OptionContractsResponse> => {
  const { url, config } = optionsContractsRequest();

  const params = new URLSearchParams({
    ...(!expiration_date && { expiration_date_gte }), // Don't need to set expiration_date_gte if expiration_date is present
    ...(expiration_date && { expiration_date }),
    ...(underlying_symbol && { underlying_symbol }),
    ...(type && { type }),
    limit: "10000",
  });

  const { data } = await instance.get(`${url}?${params.toString()}`, config);

  return looseParse(optionContractsResponseSchema, data, "getOptionsContracts");
};

export const getOptionsContract = async ({ symbol }: { symbol: string }) => {
  const { url, config } = optionsContractsRequest(`/${symbol}`);

  const res = await instance
    .get<OptionContract>(url, config)
    .catch(() => ({ data: undefined }));

  if (!res.data) {
    return res.data;
  }

  return looseParse(optionContractSchema, res.data, "getOptionsContract");
};

export interface GetOptionsSnapshotParams {
  symbols: string[];
  limit?: string;
}

export type GetOptionSnapshotsResponse = z.infer<
  typeof getOptionSnapshotsResponseSchema
>;

export const getOptionContractSnapshot = async ({
  symbols,
  limit = "100",
}: GetOptionsSnapshotParams) => {
  const params = new URLSearchParams({
    symbols: symbols.join(","),
    limit,
  });

  const res = await instance.get<GetOptionSnapshotsResponse>(`/snapshots`, {
    baseURL: window.env.OPTION_CHAIN_DATA_API_URL,
    params,
  });

  return looseParse(
    getOptionSnapshotsResponseSchema,
    res.data,
    "getOptionContractSnapshot",
  );
};

export interface GetOptionChainSnapshotsParams {
  underlying_symbol: string;
  limit?: string;
  expiration_date?: string;
  expiration_date_gte?: string;
  expiration_date_lte?: string;
}

export const getOptionChainSnapshots = async ({
  underlying_symbol,
  limit = "1000",
  expiration_date_gte = moment().format("YYYY-MM-DD"),
  expiration_date,
}: GetOptionChainSnapshotsParams) => {
  const params = new URLSearchParams({
    ...(!expiration_date && { expiration_date_gte }), // Don't need to set expiration_date_gte if expiration_date is present
    ...(expiration_date && { expiration_date }),
    limit,
  });

  const res = await instance.get<GetOptionSnapshotsResponse>(
    `/snapshots/${underlying_symbol}`,
    {
      baseURL: window.env.OPTION_CHAIN_DATA_API_URL,
      params,
    },
  );

  return looseParse(
    getOptionSnapshotsResponseSchema,
    res.data,
    "getOptionChainSnapshots",
  );
};

/** Market-data feed that served a response. */
export type OptionFeed = "indicative" | "opra";

export interface OptionChainSnapshotsWithMeta {
  snapshots: GetOptionSnapshotsResponse["snapshots"];
  /**
   * Feed the server picked, from `X-Feed`. Only set when the request omits an
   * explicit `feed` parameter, which is why this client never sends one — the
   * header is the only signal that reflects both the OPRA agreement and the
   * plan's real-time flag.
   */
  feed?: OptionFeed;
  /** Client receipt time, used to age quotes when the market is closed. */
  receivedAt: number;
}

/** Axios 0.x exposes response headers as a plain lowercase-keyed object. */
const readHeader = (
  headers: Record<string, unknown> | undefined,
  name: string,
): string | undefined => {
  const raw = headers?.[name];
  return typeof raw === "string" ? raw : undefined;
};

/** Page size the snapshots endpoint accepts. */
const OPTION_SNAPSHOT_PAGE_SIZE = "1000";

/**
 * Guards against paging forever if the endpoint keeps handing back a token.
 * Ten pages is far more than any single underlying's chain needs.
 */
const OPTION_SNAPSHOT_PAGE_LIMIT = 10;

/**
 * Same request as `getOptionChainSnapshots`, but reports which feed served the
 * response and follows `next_page_token` to the end of the chain.
 *
 * One call covers a date range, which is what keeps the chain inside the market
 * data rate limit: a request per expiration multiplied the poll by the number
 * of expirations on screen and exhausted the whole per-minute budget.
 */
export const getOptionChainSnapshotsWithMeta = async ({
  underlying_symbol,
  expiration_date,
  expiration_date_gte,
  expiration_date_lte,
}: Omit<
  GetOptionChainSnapshotsParams,
  "limit"
>): Promise<OptionChainSnapshotsWithMeta> => {
  const snapshots: GetOptionSnapshotsResponse["snapshots"] = {};
  let feedHeader: string | undefined;
  let pageToken: string | undefined;

  for (let page = 0; page < OPTION_SNAPSHOT_PAGE_LIMIT; page += 1) {
    const params = new URLSearchParams({
      ...(expiration_date && { expiration_date }),
      ...(expiration_date_gte && { expiration_date_gte }),
      ...(expiration_date_lte && { expiration_date_lte }),
      ...(pageToken && { page_token: pageToken }),
      limit: OPTION_SNAPSHOT_PAGE_SIZE,
    });

    const res = await instance.get<GetOptionSnapshotsResponse>(
      `/snapshots/${underlying_symbol}`,
      {
        baseURL: window.env.OPTION_CHAIN_DATA_API_URL,
        params,
      },
    );

    const parsed = looseParse(
      getOptionSnapshotsResponseSchema,
      res.data,
      "getOptionChainSnapshotsWithMeta",
    );

    Object.assign(snapshots, parsed.snapshots);
    // The feed is a property of the plan, so the first page settles it.
    feedHeader ??= readHeader(res.headers, "x-feed");

    if (!parsed.next_page_token) {
      break;
    }
    pageToken = parsed.next_page_token;
  }

  return {
    snapshots,
    feed:
      feedHeader === "opra" || feedHeader === "indicative"
        ? feedHeader
        : undefined,
    receivedAt: Date.now(),
  };
};
